import isEmpty from 'lodash/isEmpty';
import take from 'lodash/take';
import map from 'lodash/map';
import sumBy from 'lodash/sumBy';
import partition from 'lodash/partition';
import moment from 'moment-timezone';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';

import {truncateMiddle} from 'common/util/string';
import {serializePropFilter} from 'common/report/util/arb';
import {DateUnit, FormattedRow, RawCell, RetentionType} from 'common/report/util/retention/types';
import {
  Addiction,
  isUnsegmentedData,
  KeyedByDate,
  KeyedBySegment,
  Retention,
  RetentionData,
  SuccessResponse,
} from 'common/report/data/retention/client';
import {AGGREGATED_ROW_NAME} from 'common/report/util/retention/constants';
import {RetentionQuery} from 'common/report/data/retention/retention-query';
import {formatNumber, formatPercent} from 'common/report/util/retention/util';

const MAX_TITLE_CELL_LENGTH = 60;

export type BaseRow = {
  date: Date;
  cohort_size: number;
  data: number[];
  complete_date: Array<Date | null>;
};

export type SegmentRow = BaseRow & {
  segment: string;
};

export type Row = BaseRow | SegmentRow;
/** Aggregate row: data + optional segment, but no date */
export type AverageRow = Omit<BaseRow, 'date'> & {
  segment?: string;
  /** not calculated the same way as normal rows */
  percent_data?: number[];
};

export type SuperRow = Row | AverageRow;

/** A collection of dated rows and their aggregate, belonging to a segment. */
export type RowWithAggregate = {
  /** An aggregate of all of the rows in datedRows. */
  aggregate: AverageRow;
  /** Collection of rows for this segment, ordered by birth interval. */
  datedRows: Row[];
  /** If empty, represents unsegmented data. */
  segment?: string;
};

export function isSegmentRow(row: SuperRow): row is SegmentRow {
  return `segment` in row && `date` in row;
}

export function isAverageRow(row: SuperRow): row is AverageRow {
  return !(`date` in row);
}

export function isIntervalRow(row: SuperRow): row is BaseRow {
  return !(`segment` in row) && `date` in row;
}

export function formatDate(date: Date, timezone: string): string {
  return moment.tz(date, timezone).format(`MMM D, YYYY`);
}

function getRowHead(row: SuperRow, retentionType: RetentionType, timezone: string) {
  if (`segment` in row) {
    // prefer segment even for average rows
    return stringifySegment(row.segment);
  } else if (`date` in row) {
    return formatDate(row.date, timezone);
  } else {
    // only use this when no segment is available
    return AGGREGATED_ROW_NAME(retentionType);
  }
}

export function getCellsForRow(
  today: Date,
  query: {dateUnit: DateUnit; percentageView: boolean; retentionType: RetentionType},
  timezone: string,
) {
  return function getCellsForRow(row: SuperRow): FormattedRow<RawCell> {
    const data = row.data.map(getCellForIndex(today, row, query, timezone));
    const rowHead = getRowHead(row, query.retentionType, timezone);
    const truncatedHead =
      rowHead.length > MAX_TITLE_CELL_LENGTH ? truncateMiddle(rowHead, MAX_TITLE_CELL_LENGTH) : rowHead;

    return {
      cohort_size: row.cohort_size,
      data,
      display_cohort_size: formatNumber(row.cohort_size),
      head: rowHead,
      display_head: truncatedHead,
      raw_head: rowHead,
    };
  };
}

function stringifySegment(seg: string | string[]): string {
  if (Array.isArray(seg)) {
    return seg.join(` / `);
  } else {
    return seg;
  }
}

export function getCellForIndex(
  _today: Date,
  row: SuperRow,
  query: {percentageView: boolean; dateUnit: DateUnit; retentionType: RetentionType},
  timezone: string,
) {
  return function getCellForIndex(value: number, valIdx: number): RawCell {
    const completeDate = row.complete_date[valIdx];
    const incomplete = !!completeDate;
    let enteredValue = null;

    if (`segment` in row) {
      enteredValue = stringifySegment(row.segment);
    } else if (isAverageRow(row)) {
      enteredValue = `$average`; // this doesn't get consumed anywhere
    } else {
      enteredValue = formatDate(row.date, timezone);
    }

    let cohortSize: number, percent: number;
    if (`percent_data` in row) {
      percent = row.percent_data[valIdx];
      cohortSize = percent > 0 ? Math.round(value / percent) : 0;
    } else {
      cohortSize = row.cohort_size;
      percent = cohortSize > 0 ? value / cohortSize : 0;
    }

    return {
      cohort_size: cohortSize,
      complete_date: incomplete && moment.tz(completeDate, timezone).format(`MMM D`),
      display_value: query.percentageView ? formatPercent(percent) : formatNumber(value),
      incomplete,
      percent,
      value: query.percentageView ? percent : value,
      percent_value: formatPercent(percent),
      percent_drop_off_value: formatPercent(1 - percent),
      absolute_value: value,
      absolute_drop_off_value: cohortSize - value,
      selectable: true,
      entered_value: enteredValue,
    };
  };
}

/**
 * Turn a collection of RowWithAggregates into a flat list of rows.
 *
 * For unsegmented retention and frequency, explode the single RowWithAggregate into
 * a row for the aggregate and rows for all the birth intervals.
 *
 * For each segment of segmented retention, choose the first and only birth interval row.
 * For each segment of segmented frequency, choose the aggregate row.
 */
export function mapToUiRows(
  rowData: RowWithAggregate[],
  query: {percentageView: boolean; dateUnit: DateUnit; retentionType: RetentionType},
  today: Date,
  timezone: string,
): Array<FormattedRow<RawCell>> {
  const rowToCell = getCellsForRow(today, query, timezone);

  // We can tell by the structure of the data whether or not it's segmented.
  if (rowData.length === 1 && !isSegmentRow(rowData[0].datedRows[0])) {
    // we're in unsegmented
    const [row] = rowData;
    return [
      {
        ...rowToCell(row.aggregate),
        display_cohort_size: `100%`,
      },
      ...row.datedRows.map(rowToCell),
    ];
  } else if (query.retentionType === `addiction`) {
    return rowData.map(function (group) {
      return rowToCell(group.aggregate);
    });
  } else {
    return rowData.map(function (group) {
      const fmtRow = rowToCell(group.datedRows[0]);
      fmtRow.raw_head = fmtRow.display_head = fmtRow.head = stringifySegment(group.aggregate.segment);
      return fmtRow;
    });
  }
}

export function filtersToArbString(propertyFilters: RetentionQuery['bornFilters'] | undefined) {
  if (!propertyFilters) {
    return null;
  }

  return propertyFilters.map((pf) => serializePropFilter(pf)).join(` and `);
}

function mapToCompleteDates(
  incompleteBuckets: Record<string, string[]>,
  date: string,
  tz: string,
  fallbackLength: number,
): Array<Date | null> {
  if (incompleteBuckets && incompleteBuckets[date]) {
    return incompleteBuckets[date].map((maybeDate) =>
      typeof maybeDate === `string` ? moment.tz(maybeDate, tz).toDate() : null,
    );
  } else {
    return new Array(fallbackLength).fill(null);
  }
}

export function getSelectedSegments(
  query: RetentionQuery,
  data: RowWithAggregate[],
  context: {projectTimezone: string},
  numSelectedRows,
) {
  let selectedSegments: string[];

  if (query.isSegmented()) {
    const rows = data.map((row) => row.aggregate.segment);
    selectedSegments = rows.slice(0, numSelectedRows);
  } else {
    if (isAverageRowShowing(query)) {
      // If we can show the aggregate line, unselect everything else.
      const aggregatedRow = AGGREGATED_ROW_NAME(query.retentionType);
      selectedSegments = [aggregatedRow];
    } else {
      const [{datedRows}] = data;
      selectedSegments = sortBy(datedRows, [`date`])
        .map((row) => formatDate(row.date, context.projectTimezone))
        .slice(0, numSelectedRows);
    }
  }
  return selectedSegments;
}

/** For calculating row indices and offsets: is there an Average row showing? */
export function isAverageRowShowing(query: RetentionQuery) {
  return !query.isSegmented();
}

export function hasRetentionData(formattedRowData: RowWithAggregate[]) {
  return formattedRowData.some((row) =>
    row.datedRows.some((datedRow) => datedRow.cohort_size > 0 || datedRow.data.some((v) => v > 0)),
  );
}

function sortByAscendingDate(a: Row, b: Row) {
  return a.date.valueOf() - b.date.valueOf();
}

/**
 * Transform the API response format into Rows where
 * - dates are aligned to start of day in project time
 * - segments are renamed
 * - rows are sorted by date or size
 * - retention and addiction data looks the same
 * - aggregate data is joined to the segment it represents
 **/
export function getFormattedRowData(
  data: RetentionData,
  metadata: SuccessResponse['metadata'],
  query: RetentionQuery,
  context: {projectTimezone: string},
  rowLimit?: number,
): RowWithAggregate[] {
  if (!data || isEmpty(data)) {
    return [];
  }

  if (isUnsegmentedData(data, !query.isSegmented())) {
    const rows = map(omit(data, `$average`), function (row, date) {
      const dateInProjectTime = moment.tz(date, context.projectTimezone).toDate();
      const numbers = getRowData(row);
      return {
        ...numbers,
        date: dateInProjectTime,
        complete_date: mapToCompleteDates(
          metadata.incomplete_buckets,
          date,
          context.projectTimezone,
          numbers.data?.length,
        ),
      };
    });

    rows.sort(sortByAscendingDate);

    let averageRow: AverageRow;
    if (`$average` in data) {
      const numbers = getRowData(data[`$average`]);
      averageRow = {
        ...numbers,
        complete_date: mapToCompleteDates(
          metadata.incomplete_buckets,
          `$average`,
          context.projectTimezone,
          numbers.data?.length,
        ),
        percent_data: computeAveragePercentages(rows),
      };
    }

    return [
      {
        aggregate: averageRow,
        datedRows: rows,
      },
    ];
  }

  // segmented
  const groups = constructMultiDateRowGroups(data, metadata, context.projectTimezone);
  return rowLimit > 0 ? take(groups, rowLimit) : groups;
}

/**
 * Collect segmented rows across birth intervals into a single RowWithAggregate per segment.
 *
 * @param nullOutCompleteDates if true, _don't_ set complete_date for cells based on the
 * response data. This option is to support the historic behavior of not showing segment
 * cells as incomplete during our transition from non-intervalized segment rows to an
 * aggregate of intervalized rows.
 */
function constructMultiDateRowGroups(
  data: KeyedByDate<KeyedBySegment<any>>,
  metadata: SuccessResponse['metadata'],
  projectTimezone: string,
  nullOutCompleteDates = true,
): RowWithAggregate[] {
  const segmentMap = new Map<string, RowWithAggregate>();
  const incompleteBuckets = nullOutCompleteDates ? null : metadata.incomplete_buckets;

  for (const [bucket, , ...path] of traverseLeaves(pick(data, `$average`))) {
    const segment = path[0];
    const numbers = getRowData(bucket);

    segmentMap.set(segment, {
      aggregate: {
        ...numbers,
        segment,
        complete_date: mapToCompleteDates(incompleteBuckets, `$average`, projectTimezone, numbers.data.length),
      },
      datedRows: [],
      segment,
    });
  }
  for (const [bucket, date, ...path] of traverseLeaves(omit(data, `$average`))) {
    const segment = path[0];
    const numbers = getRowData(bucket);

    segmentMap.get(segment).datedRows.push({
      ...numbers,
      segment,
      date: moment.tz(date, projectTimezone).toDate(),
      complete_date: mapToCompleteDates(incompleteBuckets, date, projectTimezone, numbers.data.length),
    });
  }

  const groups = [...segmentMap.values()].sort(function (a, b) {
    return b.aggregate.cohort_size - a.aggregate.cohort_size;
  });

  for (const group of groups) {
    group.datedRows.sort(sortByAscendingDate);
  }

  return groups;
}

// Local helper functions

export function traverseLeaves(
  tree: KeyedByDate<KeyedBySegment<Addiction>>,
): Generator<[Addiction, string, string], void, void>;
export function traverseLeaves(
  tree: KeyedByDate<KeyedBySegment<Retention>>,
): Generator<[Retention, string, string], void, void>;

/** iteratively yield the leaves (retention or addiction data) of the response tree with their path. */
export function* traverseLeaves<TBucket>(tree: any): Generator<[TBucket, string, string], void, void> {
  function* recurse(tree: any, prefix: string[]) {
    if (isLeaf(tree)) {
      yield [tree, ...prefix];
    } else {
      for (const key of Object.keys(tree)) {
        yield* recurse(tree[key], [...prefix, key]);
      }
    }
  }

  yield* recurse(tree, []);
}

function isLeaf(value: any) {
  if (Array.isArray(value)) {
    return true;
  }

  if (typeof value === `object` && `counts` in value && `first` in value && Array.isArray(value.counts)) {
    return true;
  }

  return false;
}

function getRowData(bucket: Addiction | Retention): Pick<BaseRow, 'cohort_size' | 'data'> {
  if (Array.isArray(bucket)) {
    return {
      cohort_size: bucket[0],
      data: bucket.slice(1),
    };
  } else {
    return {
      cohort_size: bucket.first,
      data: bucket.counts,
    };
  }
}

function rowHasDataAtIndex(i: number) {
  return function (row: Row) {
    return row.data.length > i;
  };
}

/** Construct an aggregate Row from all of the date interval Rows */
function computeAveragePercentages(rows: Row[]): number[] {
  const percents = [];

  const maxIntervals = Math.max(...rows.map((r) => r.data.length));

  for (let columnIndex = 0; columnIndex < maxIntervals; columnIndex++) {
    const rowsWithData = rows.filter(rowHasDataAtIndex(columnIndex));
    const [incompleteRows, completeRows] = partition(rowsWithData, (row) => row.complete_date[columnIndex]);

    const allRowsIncomplete = completeRows.length === 0;
    const _countableRows = allRowsIncomplete ? incompleteRows : completeRows;
    const retainedTotal = sumBy(_countableRows, (row) => row.data[columnIndex]);
    const cohortTotal = sumBy(_countableRows, (row) => row.cohort_size);

    const percent = cohortTotal > 0 ? retainedTotal / cohortTotal : 0;
    percents.push(percent);
  }
  return percents;
}
